import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Helmet } from 'react-helmet-async'
import { useHistory } from 'react-router'
import { Button } from '@/components/button'
import Icon from '@/components/icon'
import Page from '@/components/page'
import PageTitle from '@/components/page-title'
import Panel from '@/components/panel'
import useProfile from '@/hooks/use-profile'
import useBetaUser from '@/hooks/use-beta-user'
import useCurrentSession from '@/hooks/use-current-session'
import { useFlag } from '@/flags'
import { Stack } from '@/components/structure'
import { H5, ParagraphRegular } from '@/components/text'
import {
  DashboardRow,
  DashboardContent,
  DashboardTitle,
  DashboardDescription
} from '@/components/settings'
import { breakpoints, fontWeights } from '@/styles'
import useDirectDeposit from '@/hooks/use-direct-deposit'
import { isNullOrEmpty } from '@/utils'
import { useBank, formatObfuscatedAccountNumber } from '@/features/Pad'
import { PlusCircleIcon } from '@/components/icons'
import Tooltip from '@/components/popper-tooltip'
import useMediaQuery from '@/hooks/use-media-query'

const StyledPage = styled(Page)`
  height: 100%;
`

const ConnectedText = styled.p`
  font-size: 20px;
  font-weight: ${fontWeights.medium};
  letter-spacing: -0.38px;
  line-height: 24px;
  display: flex;
  width: 100%;
  border-bottom: 1px solid #dfe0e0;
  padding: 0 0 16px;
  margin: 0;
  gap: 0 16px;
`

const Description = styled(ParagraphRegular)`
  padding: 20px 0 0;
`

const ContentWrapper = styled.div`
  position: relative;
  max-width: 748px;
  width: 100%;
  margin: auto;
  padding: 40px 16px 0;
  flex: 1;
`

const StyledPanel = styled(Panel)`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const StyledIcon = styled(Icon)`
  flex-shrink: 0;
`

const Row = styled.div`
  display: flex;
  width: 100%;
`

const MiddleContinaer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 40px 0 24px;
  align-items: center;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    gap: 24px 0;
    flex-direction: column;
    align-items: flex-start;
    a,
    > div:last-of-type,
    div > button > div:first-of-type,
    > div > button:first-of-type {
      width: 100%;
    }
  }
`

const TooltipWrapper = styled.div`
  p {
    max-width: 220px;
    margin: 0;
    padding: 0;
  }
`

const AddButton = styled(Button)`
  height: 36px;
  padding: 8px 16px;
  min-height: unset;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    height: 48px;
    width: 100%;
  }
`

const BankAccountSummary = ({ data }) => {
  const bank = useBank(data?.financial_info?.financial_institution_name)
  const history = useHistory()
  const accountNo = formatObfuscatedAccountNumber(data?.financial_info?.account_number, 4)
  const { t } = useTranslation()
  return (
    <StyledPanel
      hSpace={0}
      vSpace={0}
      as={props => <Panel as="button" {...props} />}
      type="button"
      onClick={() => history.push('/settings/payment-settings/direct-deposit/view')}
    >
      <DashboardRow>
        <DashboardContent bottom={0}>
          <DashboardTitle>{bank}</DashboardTitle>
          <DashboardDescription>
            {t('l.payment_settings.view.deposit_account.account_ending_in.text', {
              defaultValue: 'Account ending in {{accountNo}}',
              accountNo
            })}
          </DashboardDescription>
        </DashboardContent>
      </DashboardRow>
    </StyledPanel>
  )
}

const DirectDepositDashboard = ({ history }) => {
  const { t } = useTranslation()
  const profile = useProfile()
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const hasDirectDepositFlag = !!profile.company_info?.ach_connected.includes('upop')
  const { data } = useDirectDeposit({ enabled: hasDirectDepositFlag })
  const isLoading = !data && hasDirectDepositFlag
  const directDepositConnected = hasDirectDepositFlag && !isNullOrEmpty(data)
  const session = useCurrentSession()
  const betaUser = useBetaUser()
  const betaUserPad = betaUser.pad
  const currentBetaUser = betaUserPad.includes(parseInt(session.id))
  const showPad = useFlag(['features', 'pad'], false) && currentBetaUser
  return (
    <StyledPage>
      <ContentWrapper>
        <Helmet>
          <title>{t('l.payment_settings.view.direct_deposit.title', 'Direct Deposit')}</title>
        </Helmet>
        <PageTitle>{t('l.payment_settings.view.direct_deposit.title', 'Direct Deposit')}</PageTitle>
        {!isLoading && (
          <>
            {directDepositConnected ? (
              <StyledPanel hSpace={32} vSpace={32}>
                <Row>
                  <ConnectedText>
                    <StyledIcon id="checked_green" width={24} height={24} />
                    {t(
                      'l.payment_settings.view.deposit_account_connected.text',
                      'Deposit account connected!'
                    )}
                  </ConnectedText>
                </Row>
                <Row>
                  <Description>
                    {showPad
                      ? t(
                          'l.payment_settings.view.direct_deposit_connected.description',
                          `You're now able to receive Pre-Authorized Debit (PAD) and Bitcoin payments. To edit this account info, please remove the current one, and re-enter your new info.`
                        )
                      : t(
                          'l.payment_settings.view.direct_deposit_connected_bitcoin.description',
                          `You're now able to receive Bitcoin payments exchanged into your currency. To edit this account info, please remove the current one, and re-enter your new info.`
                        )}
                  </Description>
                </Row>
              </StyledPanel>
            ) : (
              <>
                {showPad && (
                  <StyledPanel hSpace={32} vSpace={32}>
                    <Row>
                      <ParagraphRegular>
                        {t(
                          'l.payment_settings.view.connect_direct_deposit.description',
                          'Connect your banking information to receive automatic rent payments through Pre-Authorized Debit (PAD) and one-time payments through Bitcoin.'
                        )}
                      </ParagraphRegular>
                    </Row>
                  </StyledPanel>
                )}
              </>
            )}
            <MiddleContinaer>
              <H5>
                {t(
                  'l.payment_settings.view.direct_deposit.multi_account.title',
                  'Direct Deposit Accounts'
                )}
              </H5>
              <Tooltip
                theme="secondary"
                enabled={directDepositConnected}
                size="medium"
                content={
                  <TooltipWrapper>
                    <p>
                      {t(
                        'l.payment_settings.view.direct_deposit.multi_account.tooltip',
                        'We will be supporting multiple bank accounts soon. Stay tuned!'
                      )}
                    </p>
                  </TooltipWrapper>
                }
                placement={displayMobile ? 'top' : 'bottom-end'}
              >
                <AddButton
                  theme="secondary"
                  size="small"
                  disabled={directDepositConnected}
                  aria-disabled={directDepositConnected}
                  to="/settings/payment-settings/direct-deposit/add"
                >
                  <PlusCircleIcon />{' '}
                  {t(
                    'l.payment_settings.view.direct_deposit.add_account.button',
                    'Add New Account'
                  )}
                </AddButton>
              </Tooltip>
            </MiddleContinaer>
            <Stack gutter={24}>
              {directDepositConnected ? (
                <BankAccountSummary data={data} />
              ) : (
                <StyledPanel hSpace={0} vSpace={0}>
                  <DashboardRow>
                    <DashboardContent bottom={0}>
                      <DashboardDescription>
                        {t(
                          'l.payment_settings.view.direct_deposit.no_accounts.text',
                          'No accounts added'
                        )}
                      </DashboardDescription>
                    </DashboardContent>
                  </DashboardRow>
                </StyledPanel>
              )}
            </Stack>
          </>
        )}
      </ContentWrapper>
    </StyledPage>
  )
}

export default DirectDepositDashboard
